//
// Copyright (c) 2020 Astute Global, Inc.
//

define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
  "modules/shared/history/historyModel",
], function (Backbone, $, _, Messenger, Properties, SortableListModel, HistoryModel) {
  "use strict";

  const TableOfContentsModel = {};

  TableOfContentsModel.level = Backbone.Model.extend({
    defaults: {
      tableOfContentsId: 0,
      categoryId: 0,
      categoryName: "",
      order: 0,
    },
  });

  TableOfContentsModel.levels = Backbone.AtomicCollection.extend({
    model: TableOfContentsModel.level,
    url: function () {
      const tableOfContents = this.tableOfContents;
      if (!tableOfContents || tableOfContents.isNew()) {
        return "";
      }
      const baseUrl = tableOfContents.url();
      if (baseUrl.endsWith("/")) {
        return baseUrl + "levels";
      }
      return baseUrl + "/levels";
    },
    comparator: function (a, b) {
      return Backbone.numericComparator(a, b, "order");
    },
    addLevel: function (category) {
      const existingLevel = this.findWhere({ categoryId: category.id });
      if (existingLevel) {
        return existingLevel;
      }
      let order;
      if (this.length === 0) {
        order = 0;
      } else {
        order = this.at(this.length - 1).get("order") + 1;
      }
      const levelData = {
        categoryId: category.get("categoryId"),
        categoryName: category.get("categoryName"),
        order: order,
      };
      if (!this.tableOfContents.isNew()) {
        levelData.tableOfContentsId = this.tableOfContents.id;
      }
      const levelModel = new TableOfContentsModel.level(levelData);
      levelModel.tableOfContents = this.tableOfContents;
      this.add(levelModel);
      this.tableOfContents.dirty = true;
      return levelModel;
    },
    moveDown: function (level) {
      let existingLevel;
      if (level.isNew()) {
        existingLevel = this.findWhere({ categoryId: level.get("categoryId") });
      } else {
        existingLevel = this.get(level.id);
      }
      if (!existingLevel) {
        return;
      }
      const index = this.indexOf(existingLevel);
      if (index === this.length - 1) {
        return;
      }
      const originalOrder = existingLevel.get("order");
      const newOrder = originalOrder + 1;
      const otherLevel = this.findWhere({ order: newOrder });
      if (otherLevel) {
        otherLevel.set("order", originalOrder);
      }
      existingLevel.set("order", newOrder);
      this.sort();
    },
    moveUp: function (level) {
      let existingLevel;
      if (level.isNew()) {
        existingLevel = this.findWhere({ categoryId: level.get("categoryId") });
      } else {
        existingLevel = this.get(level.id);
      }
      if (!existingLevel) {
        return;
      }
      const index = this.indexOf(existingLevel);
      if (index === 0) {
        return;
      }
      const originalOrder = existingLevel.get("order");
      const newOrder = originalOrder - 1;
      const otherLevel = this.findWhere({ order: newOrder });
      if (otherLevel) {
        otherLevel.set("order", originalOrder);
      }
      existingLevel.set("order", newOrder);
      this.sort();
    },
  });

  TableOfContentsModel.tableOfContents = Backbone.Model.extend({
    defaults: function () {
      const now = new Date();
      const currentUser = Messenger.reqres.request("user");
      return {
        name: "",
        kbId: Messenger.reqres.request("active:kb"),
        createDate: now,
        createUserId: currentUser.get("userId"),
        createUserName: currentUser.get("userName"),
        lastUpdateDate: now,
        lastUpdateUserId: currentUser.get("userId"),
        lastUpdateUserName: currentUser.get("userName"),
      };
    },
    url: function () {
      let kbId = this.get("kbId");
      if (!kbId) {
        kbId = Messenger.reqres.request("active:kb");
        if (!kbId) {
          return "";
        }
      }
      let url = Properties.rootURL + "/" + kbId + "/tableOfContents";
      if (!this.isNew()) {
        url += "/" + this.id;
      }
      return url;
    },
    history: function () {
      const history = new HistoryModel.historyCollection();
      if (!this.isNew()) {
        const url = this.url() + "/history";
        history.url = function () {
          return url;
        };
        history.fetch();
      }
      return history;
    },
    levels: function () {
      this.levelsCollection = this.getSubModel(
        this.levelsCollection,
        TableOfContentsModel.levels,
        "/levels",
      );
      this.levelsCollection.tableOfContents = this;
      return this.levelsCollection;
    },
    saveChildren: function (options) {
      this.saveSubModels(options);
    },
    validateAttribute: function (attribute, value) {
      if (attribute == "name") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.tableOfContents.noName",
            maxLength: 255,
          }) || this.validateSafeCharacters(value)
        );
      }
    },
  });

  TableOfContentsModel.tableOfContentsCollection = Backbone.Collection.extend({
    model: TableOfContentsModel.tableOfContents,
    url: function () {
      let kbId = this.kbId;
      if (!kbId) {
        kbId = Messenger.reqres.request("active:kb");
        if (!kbId) {
          return "";
        }
      }
      return Properties.rootURL + "/" + kbId + "/tableOfContents";
    },
    addItem: function () {
      const tableOfContents = new TableOfContentsModel.tableOfContents({
        kbId: this.kbId || Messenger.reqres.request("active:kb"),
      });
      this.add(tableOfContents);
      return tableOfContents;
    },
  });

  //Request for the headers of the topic examples history list
  Messenger.reqres.setHandler("tableOfContents:history:headers", function (headerType) {
    const Header = SortableListModel.headerModel;
    if (headerType === "detailModal") {
      return new Backbone.Collection([
        new Header({
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          textField: true,
        }),
        new Header({
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          textField: true,
        }),
      ]);
    } else {
      return new Backbone.Collection([
        new Header({
          headerName: $.t("history.user"),
          modelAttribute: "userName",
          sortable: true,
          filterable: true,
          textField: true,
          width: "15%",
        }),
        new Header({
          headerName: $.t("history.date"),
          modelAttribute: "dateTime",
          defaultSort: true,
          defaultReversed: true,
          sortable: true,
          dateField: true,
        }),
        new Header({
          headerName: $.t("history.change"),
          modelAttribute: "changeType",
          sortable: true,
          filterable: true,
          textField: true,
          width: "15%",
        }),
        new Header({
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          sortable: true,
          textField: true,
        }),
        new Header({
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          sortable: true,
          textField: true,
        }),
      ]);
    }
  });

  return TableOfContentsModel;
});
